import type { TableColumn } from "@swot/base-components";
import type { CmtsExportVm, CmtsReportDefinitionVm, CmtsReportVm } from "../view-models/reportViewModels.js";
import { readArray, readBoolean, readNumber, readString } from "./mapperUtils.js";

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? value as Record<string, unknown> : {};
}

export function mapTypedReport(dto: unknown, reportCode: string): CmtsReportVm {
  const rows = readArray(dto, ["rows", "items"], (x) => ({ ...asRecord(x) }));
  const metrics = readArray(dto, ["metrics"], (x) => ({ ...asRecord(x) }));
  return {
    reportCode: readString(dto, ["reportCode"], reportCode),
    rows,
    columns: inferColumns(rows),
    totalCount: readNumber(dto, ["totalCount"], rows.length),
    pageNumber: readNumber(dto, ["pageNumber"], 1),
    pageSize: readNumber(dto, ["pageSize"], rows.length || 25),
    totalPages: readNumber(dto, ["totalPages"], rows.length > 0 ? 1 : 0),
    metrics,
    filters: asRecord(asRecord(dto).filters),
    diagnostics: rows.length === 0 ? [{ code: "CMTS_REPORT_EMPTY", message: "The report returned no rows for the selected filter.", severity: "info" }] : [],
  };
}

export function mapReportDefinition(dto: unknown): CmtsReportDefinitionVm {
  const record = asRecord(dto);
  const code = readString(record, ["code", "reportCode", "reportId"], readString(record, ["id"], "REPORT"));
  const name = readString(record, ["name", "title", "report"], code);
  return {
    id: readString(record, ["id", "reportId"], code),
    tenantId: readString(record, ["tenantId"], ""),
    code,
    name,
    title: readString(record, ["title"], name),
    description: readString(record, ["description"], "Repository-backed CMTS report definition."),
    reportType: readString(record, ["reportType", "type"], "TYPED"),
    reportCategory: readString(record, ["reportCategory", "category"], "Complaint Reports"),
    sourceModule: readString(record, ["sourceModule"], "COMPLAINT"),
    permissionCode: readString(record, ["permissionCode", "permission"], "REPORT.EXPORT"),
    status: readString(record, ["status"], readBoolean(record, ["active", "isActive"], true) ? "ACTIVE" : "INACTIVE"),
    active: readBoolean(record, ["active", "isActive"], true),
    filterSchema: asRecord(record.filterSchema),
    columns: readArray(record, ["columns", "availableColumns"], (x) => String(x)),
    visibleColumns: readArray(record, ["visibleColumns", "columns"], (x) => String(x)),
    metrics: readArray(record, ["metrics", "metricDefinitions"], (x) => String(x)),
    exportFormats: readArray(record, ["exportFormats"], (x) => String(x)),
    drilldownRoute: readString(record, ["drilldownRoute", "targetRoute"], "/cmts/reports/typed"),
    drilldownLinks: readArray(record, ["drilldownLinks"], (x) => ({ ...asRecord(x) })),
    dataScope: readString(record, ["dataScope"], "TENANT_PERSONA"),
    sourceEntityType: readString(record, ["sourceEntityType"], readString(record, ["sourceModule"], "COMPLAINT")),
    diagnostics: [],
  };
}

export function mapExport(dto: unknown): CmtsExportVm {
  return {
    exportId: readString(dto, ["exportId", "id"], ""),
    fileName: readString(dto, ["fileName"], "cmts-export.json"),
    status: readString(dto, ["status"], "UNKNOWN"),
    rowCount: readNumber(dto, ["rowCount", "resultCount"], 0),
    downloadReady: readBoolean(dto, ["downloadReady"], false),
    checksum: readString(dto, ["checksum"], ""),
    reportCode: readString(dto, ["reportCode"], ""),
    exportFormat: readString(dto, ["exportFormat", "format"], "JSON"),
    exportedAt: readString(dto, ["exportedAt", "requestedAt", "requestedAtUtc", "createdAt"], ""),
    exportedBy: readString(dto, ["exportedBy", "requestedByUserId", "createdBy"], ""),
    simulated: readBoolean(dto, ["simulated", "offlineSimulated"], true),
    failureReason: readString(dto, ["failureReason"], ""),
    auditEventId: readString(dto, ["auditEventId"], ""),
    operationSpineEventId: readString(dto, ["operationSpineEventId"], ""),
    diagnostics: [],
  };
}

export function inferColumns(rows: readonly Record<string, unknown>[]): readonly TableColumn<Record<string, unknown>>[] {
  const preferred = ["code", "name", "reportCategory", "sourceModule", "status", "complaintNumber", "customerName", "supplierName", "department", "slaStatus", "rcaStatus", "capaStatus", "verificationStatus", "rowCount", "exportFormat", "fileName", "exportedAt"];
  const keys = new Set<string>();
  for (const key of preferred) if (rows.some((row) => row[key] !== undefined)) keys.add(key);
  for (const row of rows.slice(0, 20)) Object.keys(row).forEach((k) => keys.add(k));
  return [...keys].map((key) => ({ id: key, header: toTitle(key), sortable: true, filterable: true }));
}
function toTitle(key: string): string { return key.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase()); }
